import React, { useState, useEffect } from 'react';
import { UserLayout } from '../../components/user/UserLayout';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { 
  ArrowLeft, User, Mail, Phone, Calendar, Globe, Heart, AlertTriangle, 
  Utensils, Wine, MessageSquare, Clock, History, Pencil, X, Save, Gift
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const GuestProfileDetails = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchProfile();
  }, [profileId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/guest-profiles/detail/${profileId}`);
      if (response.data.success) {
        setProfile(response.data.profile);
        setEditFormData({
          countryCode: response.data.profile.countryCode || '',
          phoneNumber: response.data.profile.phoneNumber || '',
          dateOfBirth: response.data.profile.dateOfBirth || '',
          nationality: response.data.profile.nationality || '',
          numVisits: response.data.profile.numVisits || 0,
          preferences: response.data.profile.preferences || '',
          allergies: response.data.profile.allergies || '',
          favouriteDrink: response.data.profile.favouriteDrink || '',
          favouriteFood: response.data.profile.favouriteFood || '',
          likesAndDislikes: response.data.profile.likesAndDislikes || '',
          birthday: response.data.profile.birthday || '',
          anniversary: response.data.profile.anniversary || '',
          additionalComments: response.data.profile.additionalComments || ''
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast({
        title: "Error",
        description: "Failed to load guest profile",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      
      const payload = {
        ...editFormData,
        updatedByName: user?.name || 'Unknown User',
        updatedByEmail: user?.email || 'unknown@email.com'
      };
      
      const response = await axios.put(
        `${BACKEND_URL}/api/guest-profiles/${profileId}`,
        payload
      );

      if (response.data.success) {
        toast({
          title: "Success",
          description: "Guest profile updated successfully"
        });
        setProfile(response.data.profile);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update guest profile",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      countryCode: 'Country Code',
      phoneNumber: 'Phone Number',
      dateOfBirth: 'Date of Birth',
      nationality: 'Nationality',
      numVisits: 'Number of Visits',
      preferences: 'Preferences',
      allergies: 'Allergies',
      favouriteDrink: 'Favourite Drink',
      favouriteFood: 'Favourite Food',
      likesAndDislikes: 'Likes & Dislikes',
      birthday: 'Birthday',
      anniversary: 'Anniversary',
      additionalComments: 'Additional Comments'
    };
    return labels[field] || field;
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading guest profile...</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  if (!profile) {
    return (
      <UserLayout>
        <div className="text-center py-12">
          <p className="text-slate-600">Guest profile not found</p>
          <Button onClick={() => navigate(-1)} className="mt-4">
            Go Back
          </Button>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="hover:bg-slate-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-xl">
                  {profile.firstName?.charAt(0)}{profile.lastName?.charAt(0) || ''}
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">
                  {profile.firstName} {profile.lastName || ''}
                </h1>
                <p className="text-slate-500">{profile.email}</p>
              </div>
            </div>
          </div>
          
          {!isEditing ? (
            <Button
              onClick={() => setIsEditing(true)}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Pencil className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          ) : (
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={saveProfile}
                disabled={saving}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                <User className="w-5 h-5 mr-2 text-teal-500" />
                Basic Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-500">First Name</Label>
                  <p className="text-slate-800 font-medium">{profile.firstName}</p>
                </div>
                <div>
                  <Label className="text-sm text-slate-500">Last Name</Label>
                  <p className="text-slate-800 font-medium">{profile.lastName || '-'}</p>
                </div>
                <div>
                  <Label className="text-sm text-slate-500">Email</Label>
                  <p className="text-slate-800 font-medium">{profile.email}</p>
                </div>
                <div>
                  <Label className="text-sm text-slate-500">Phone</Label>
                  {isEditing ? (
                    <div className="flex gap-2">
                      <Input
                        value={editFormData.countryCode}
                        onChange={(e) => handleEditFormChange('countryCode', e.target.value)}
                        placeholder="+1"
                        className="w-20"
                      />
                      <Input
                        value={editFormData.phoneNumber}
                        onChange={(e) => handleEditFormChange('phoneNumber', e.target.value)}
                        placeholder="Phone number"
                      />
                    </div>
                  ) : (
                    <p className="text-slate-800 font-medium">
                      {profile.countryCode || profile.phoneNumber ? `${profile.countryCode || ''} ${profile.phoneNumber || ''}` : '-'}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-sm text-slate-500">Date of Birth</Label>
                  {isEditing ? (
                    <Input
                      type="date"
                      value={editFormData.dateOfBirth}
                      onChange={(e) => handleEditFormChange('dateOfBirth', e.target.value)}
                    />
                  ) : (
                    <p className="text-slate-800 font-medium">{profile.dateOfBirth || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-sm text-slate-500">Nationality</Label>
                  {isEditing ? (
                    <Input
                      value={editFormData.nationality}
                      onChange={(e) => handleEditFormChange('nationality', e.target.value)}
                      placeholder="Nationality"
                    />
                  ) : (
                    <p className="text-slate-800 font-medium">{profile.nationality || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-sm text-slate-500">Number of Visits</Label>
                  {isEditing ? (
                    <Input
                      type="number"
                      value={editFormData.numVisits}
                      onChange={(e) => handleEditFormChange('numVisits', parseInt(e.target.value) || 0)}
                    />
                  ) : (
                    <p className="text-slate-800 font-medium">{profile.numVisits || 0}</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Preferences & Allergies */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                <Heart className="w-5 h-5 mr-2 text-pink-500" />
                Preferences & Allergies
              </h2>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-slate-500 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1 text-amber-500" />
                    Allergies
                  </Label>
                  {isEditing ? (
                    <Input
                      value={editFormData.allergies}
                      onChange={(e) => handleEditFormChange('allergies', e.target.value)}
                      placeholder="Any allergies"
                    />
                  ) : (
                    <p className="text-slate-800">{profile.allergies || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-sm text-slate-500">Preferences</Label>
                  {isEditing ? (
                    <Input
                      value={editFormData.preferences}
                      onChange={(e) => handleEditFormChange('preferences', e.target.value)}
                      placeholder="Guest preferences"
                    />
                  ) : (
                    <p className="text-slate-800">{profile.preferences || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-sm text-slate-500">Likes & Dislikes</Label>
                  {isEditing ? (
                    <Input
                      value={editFormData.likesAndDislikes}
                      onChange={(e) => handleEditFormChange('likesAndDislikes', e.target.value)}
                      placeholder="Likes and dislikes"
                    />
                  ) : (
                    <p className="text-slate-800">{profile.likesAndDislikes || '-'}</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Favourites */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                <Utensils className="w-5 h-5 mr-2 text-orange-500" />
                Favourites
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-500 flex items-center">
                    <Wine className="w-4 h-4 mr-1 text-purple-500" />
                    Favourite Drink
                  </Label>
                  {isEditing ? (
                    <Input
                      value={editFormData.favouriteDrink}
                      onChange={(e) => handleEditFormChange('favouriteDrink', e.target.value)}
                      placeholder="Favourite drink"
                    />
                  ) : (
                    <p className="text-slate-800">{profile.favouriteDrink || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-sm text-slate-500 flex items-center">
                    <Utensils className="w-4 h-4 mr-1 text-orange-500" />
                    Favourite Food
                  </Label>
                  {isEditing ? (
                    <Input
                      value={editFormData.favouriteFood}
                      onChange={(e) => handleEditFormChange('favouriteFood', e.target.value)}
                      placeholder="Favourite food"
                    />
                  ) : (
                    <p className="text-slate-800">{profile.favouriteFood || '-'}</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Special Dates */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                <Gift className="w-5 h-5 mr-2 text-red-500" />
                Special Dates
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-500">Birthday</Label>
                  {isEditing ? (
                    <Input
                      type="date"
                      value={editFormData.birthday}
                      onChange={(e) => handleEditFormChange('birthday', e.target.value)}
                    />
                  ) : (
                    <p className="text-slate-800">{profile.birthday || '-'}</p>
                  )}
                </div>
                <div>
                  <Label className="text-sm text-slate-500">Anniversary</Label>
                  {isEditing ? (
                    <Input
                      type="date"
                      value={editFormData.anniversary}
                      onChange={(e) => handleEditFormChange('anniversary', e.target.value)}
                    />
                  ) : (
                    <p className="text-slate-800">{profile.anniversary || '-'}</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Additional Comments */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-blue-500" />
                Additional Comments
              </h2>
              {isEditing ? (
                <textarea
                  value={editFormData.additionalComments}
                  onChange={(e) => handleEditFormChange('additionalComments', e.target.value)}
                  placeholder="Additional comments about the guest"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 min-h-[100px]"
                />
              ) : (
                <p className="text-slate-800">{profile.additionalComments || 'No additional comments'}</p>
              )}
            </Card>
          </div>

          {/* Update History Timeline */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-6">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                <History className="w-5 h-5 mr-2 text-indigo-500" />
                Update History
              </h2>
              
              {profile.updateHistory && profile.updateHistory.length > 0 ? (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {[...profile.updateHistory].reverse().map((entry, index) => (
                    <div key={index} className="relative pl-6 pb-4 border-l-2 border-slate-200 last:pb-0">
                      <div className="absolute -left-2 top-0 w-4 h-4 bg-indigo-500 rounded-full border-2 border-white"></div>
                      
                      <div className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-700">{entry.updatedBy}</span>
                          <span className="text-xs text-slate-500">{formatDate(entry.timestamp)}</span>
                        </div>
                        
                        <div className="space-y-1">
                          {Object.entries(entry.changes || {}).map(([field, change]) => (
                            <div key={field} className="text-xs">
                              <span className="font-medium text-slate-600">{getFieldLabel(field)}:</span>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-red-500 line-through">{change.old || '(empty)'}</span>
                                <span className="text-slate-400">→</span>
                                <span className="text-green-600">{change.new || '(empty)'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No update history yet</p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </UserLayout>
  );
};
